/**
 * useFunnelSchemaVersions
 *
 * Lazy-fetches Web Placement schema support for the (funnelId, versionId) pairs
 * referenced by a list of WebPlacements.
 *
 * Performance:
 * - Dedupes by `${funnelId}::${versionId}` so 50 placements pointing to the
 *   same 5 versions issue 5 reads, not 50.
 * - In-flight cache (useRef) survives placement array reference changes —
 *   re-mounting the component clears the cache, but rendering with a new
 *   `placements` reference does not re-fetch already-resolved pairs.
 * - Errors do not propagate — each pair lives in `'loading' | 'v2'
 *   | 'unsupported' | 'error'` independently.
 *
 * Returns a Map keyed by WebPlacement.id (not by funnelId pair) for direct
 * row-level lookup in the list UI.
 *
 * @module webplacements/hooks
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  detectSchemaVersion,
  type SchemaVersionResult,
} from '@modules/webplacements/services/schemaVersionDetector';
import type { WebPlacement } from '@modules/webplacements/types/webplacements.types';

export type SchemaState = 'loading' | SchemaVersionResult;

export interface UseFunnelSchemaVersionsResult {
  byPlacement: Map<string, SchemaState>;
  allResolved: boolean;
}

interface CacheEntry {
  promise: Promise<SchemaVersionResult>;
  result?: SchemaVersionResult;
}

/**
 * Settled reads are projected at most once per interval while others are still
 * pending (and immediately when the last one lands). Projecting after every read
 * re-renders every row once per read: O(n²) on a list with thousands of placements.
 */
export const SCHEMA_FLUSH_INTERVAL_MS = 500;

const pairKey = (funnelId: string, versionId: string): string => `${funnelId}::${versionId}`;

const placementPair = (wp: WebPlacement): { funnelId: string; versionId: string } | null => {
  const funnelId = wp.config?.funnelId;
  const versionId = wp.config?.versionId;
  if (!funnelId || !versionId) {
    return null;
  }
  return { funnelId, versionId };
};

export function useFunnelSchemaVersions(
  projectId: string,
  placements: WebPlacement[]
): UseFunnelSchemaVersionsResult {
  /**
   * Per-pair cache survives placement array ref changes inside one mount.
   * Cleared automatically when the component unmounts.
   */
  const cacheRef = useRef<Map<string, CacheEntry>>(new Map());

  /**
   * Map from WebPlacement.id → resolution state. Recomputed on every render
   * from the cache + the current `placements` ref (cheap projection, no fetch).
   */
  const [byPlacement, setByPlacement] = useState<Map<string, SchemaState>>(new Map());

  /**
   * Stable signature of the current placement set — when this string changes,
   * we kick off lazy-fetch for any newly-seen pair. We hash by id|funnelId|versionId
   * so adding a new placement to the same funnel does NOT re-fetch.
   */
  const placementSignature = useMemo(() => {
    return placements
      .map((wp) => `${wp.id}|${wp.config?.funnelId ?? ''}|${wp.config?.versionId ?? ''}`)
      .sort()
      .join(',');
  }, [placements]);

  useEffect(() => {
    if (!projectId || placements.length === 0) {
      setByPlacement(new Map());
      return;
    }

    let cancelled = false;
    const cache = cacheRef.current;

    /**
     * Build the next snapshot from cache. Each placement gets the cached
     * result if present, or `'loading'` while its pair's promise is pending.
     */
    const projectFromCache = (): Map<string, SchemaState> => {
      const out = new Map<string, SchemaState>();
      for (const wp of placements) {
        const pair = placementPair(wp);
        if (!pair) {
          out.set(wp.id, 'error');
          continue;
        }
        const entry = cache.get(pairKey(pair.funnelId, pair.versionId));
        out.set(wp.id, entry?.result ?? 'loading');
      }
      return out;
    };

    // 1) Initial paint: render everything we already know + 'loading' for the rest.
    setByPlacement(projectFromCache());

    // 2) Kick off fetches for any pair we haven't seen before.
    const newPromises: Array<Promise<void>> = [];
    for (const wp of placements) {
      const pair = placementPair(wp);
      if (!pair) continue;
      const key = pairKey(pair.funnelId, pair.versionId);
      if (cache.has(key)) continue;

      const fetchPromise = detectSchemaVersion(projectId, pair.funnelId, pair.versionId);
      const entry: CacheEntry = { promise: fetchPromise };
      cache.set(key, entry);

      newPromises.push(
        fetchPromise.then((result) => {
          entry.result = result;
        }).catch((error) => {
          // detectSchemaVersion never rejects, but defend against future regressions.
          console.error('[useFunnelSchemaVersions] unexpected detector rejection', error);
          entry.result = 'error';
        })
      );
    }

    // 3) When new fetches land, re-project — throttled to one projection per
    //    SCHEMA_FLUSH_INTERVAL_MS while others are pending, so loading states
    //    still drain incrementally, and immediately once the last one settles.
    let flushTimer: ReturnType<typeof setTimeout> | null = null;
    const flush = () => {
      flushTimer = null;
      if (!cancelled) {
        setByPlacement(projectFromCache());
      }
    };
    let pending = newPromises.length;
    newPromises.forEach((p) => {
      p.then(() => {
        pending -= 1;
        if (cancelled) return;
        if (pending === 0) {
          if (flushTimer) clearTimeout(flushTimer);
          flush();
        } else if (!flushTimer) {
          flushTimer = setTimeout(flush, SCHEMA_FLUSH_INTERVAL_MS);
        }
      });
    });

    return () => {
      cancelled = true;
      if (flushTimer) clearTimeout(flushTimer);
    };
    // We intentionally depend on the *signature*, not on the placements array
    // reference — re-renders with the same set must NOT trigger re-fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, placementSignature]);

  const allResolved = useMemo(() => {
    if (placements.length === 0) return true;
    for (const state of byPlacement.values()) {
      if (state === 'loading') return false;
    }
    return byPlacement.size === placements.length;
  }, [byPlacement, placements.length]);

  return { byPlacement, allResolved };
}
